import { useState } from 'react';
import type { FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api';
import type { Counter, CounterKey, SessionUser } from '../api';
import { usePlainLanguage } from '../plainLanguage';
import {
  ArchiveButton,
  ArchivedToggle,
  Breadcrumbs,
  Button,
  Card,
  ConfirmButton,
  Empty,
  ErrorBanner,
  Field,
  InlineEdit,
  Modal,
  ModalActions,
  PageHead,
  PageHeadRow,
  ShowOnce,
  StatusPill,
  TableScroll,
  formatDate,
  useAction,
  useLoad,
} from '../ui';

/**
 * One outlet and its counters, on their own page.
 *
 * Counters used to expand inline inside a list of every outlet, which pushed the rest
 * of the page down and gave no sense of where you were. A counter belongs to exactly
 * one outlet, so it gets the outlet's page rather than a fold inside a shared one.
 */
export function OutletDetail({ user }: { user: SessionUser }) {
  const params = useParams();
  const orgId = params.orgId ?? '';
  const outletId = params.outletId ?? '';
  const isStaff = user.role === 'staff';
  const plain = usePlainLanguage();

  const outlet = useLoad(() => api.outlets.get(outletId), [outletId]);
  const action = useAction();

  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const counters = useLoad(
    () => api.counters.list(outletId, showArchived),
    [outletId, showArchived],
  );

  return (
    <>
      <Breadcrumbs
        trail={[
          ...(isStaff ? [{ label: 'Organizations', to: '/organizations' }] : []),
          {
            label: outlet.data?.org.name ?? '…',
            to: `/organizations/${orgId}/outlets`,
          },
          { label: outlet.data?.name ?? '…' },
        ]}
      />

      <PageHead
        title={outlet.data?.name ?? (plain ? 'Shop' : 'Outlet')}
        hint={
          plain
            ? 'A counter is one till PC. Connect it once with a code and it stays connected.'
            : 'A counter is one till PC. Each pairs itself once and then holds its own API key.'
        }
        actions={
          outlet.data && (
            <>
              <StatusPill status={outlet.data.status} />
              <ArchiveButton
                status={outlet.data.status}
                label={outlet.data.name}
                cascadeNote={
                  plain
                    ? 'Its counters stop taking mobile payments.'
                    : 'Its counters are archived with it and stop taking payments.'
                }
                busy={action.busy}
                onArchive={() =>
                  void action.run(() => api.outlets.archive(outletId)).then(() => {
                    outlet.reload();
                    counters.reload();
                  })
                }
                onRestore={() =>
                  void action.run(() => api.outlets.restore(outletId)).then(() => {
                    outlet.reload();
                    counters.reload();
                  })
                }
              />
            </>
          )
        }
      />

      <ErrorBanner message={outlet.error ?? counters.error ?? action.error} />

      {/* Staff only: nothing reads the MPOS shop id yet, and every shop is on Maldives time. */}
      {outlet.data && !plain && (
        <Card title="Outlet details">
          <InlineEdit
            label="MPOS Shop ID"
            value={outlet.data.mposShopId}
            placeholder="001"
            hint="[Shop Master].[Shop ID] in this customer's MPOS database. Nothing uses it yet — it is recorded for matching receipts later."
            busy={action.busy}
            onSave={(next) =>
              void action
                .run(() => api.outlets.update(outletId, { mposShopId: next }))
                .then(outlet.reload)
            }
          />
          <InlineEdit
            label="Timezone"
            value={outlet.data.timezone}
            placeholder="Indian/Maldives"
            busy={action.busy}
            onSave={(next) =>
              void action
                .run(() => api.outlets.update(outletId, { timezone: next || undefined }))
                .then(outlet.reload)
            }
          />
        </Card>
      )}

      <div className="mb-3.5">
        <PageHeadRow
          actions={
            <>
              <ArchivedToggle showArchived={showArchived} onChange={setShowArchived} />
              <Button onClick={() => setCreating(true)}>
                {plain ? 'Add a counter' : 'New counter'}
              </Button>
            </>
          }
        >
          <h2 className="text-[17px] font-semibold">Counters</h2>
        </PageHeadRow>
      </div>

      <Modal open={creating} title="Add a counter" onClose={() => setCreating(false)}>
        <NewCounter
          outletId={outletId}
          onCreated={() => {
            setCreating(false);
            counters.reload();
          }}
        />
      </Modal>

      {counters.loading && <Empty>Loading counters…</Empty>}
      {!counters.loading && counters.data?.length === 0 && (
        <Card>
          <Empty>
            {showArchived
              ? 'No counters, archived or otherwise.'
              : plain
                ? 'No counters in this shop yet.'
                : 'No counters on this outlet yet.'}
          </Empty>
        </Card>
      )}

      {(counters.data ?? []).map((counter) => (
        <CounterCard key={counter.id} counter={counter} onChanged={counters.reload} />
      ))}
    </>
  );
}

/** A counter needs a name. Everything else about it is an edit on its own card. */
function NewCounter({ outletId, onCreated }: { outletId: string; onCreated: () => void }) {
  const [name, setName] = useState('');
  const action = useAction();

  async function create(event: FormEvent) {
    event.preventDefault();
    const ok = await action.run(() => api.counters.create({ outletId, name: name.trim() }));
    if (ok) {
      setName('');
      onCreated();
    }
  }

  return (
    <form onSubmit={create} className="flex flex-col gap-3">
      <ErrorBanner message={action.error} />
      <Field label="Counter name">
        <input
          required
          autoFocus
          value={name}
          placeholder="Counter 1"
          onChange={(event) => setName(event.target.value)}
        />
      </Field>
      <ModalActions>
        <Button type="submit" disabled={action.busy}>
          {action.busy ? 'Adding…' : 'Add counter'}
        </Button>
      </ModalActions>
    </form>
  );
}

function CounterCard({ counter, onChanged }: { counter: Counter; onChanged: () => void }) {
  const [pairing, setPairing] = useState<{ code: string; expiresAt: string } | null>(null);
  const [issued, setIssued] = useState<{ apiKey: string; keyId: string } | null>(null);
  const action = useAction();
  const plain = usePlainLanguage();

  const live = (counter.apiKeys ?? []).filter((key) => !key.revokedAt);
  const archived = counter.status === 'archived';

  return (
    <Card
      title={counter.name}
      action={
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={counter.status} />
          {!archived && (
            <>
              <Button
                variant="ghost"
                disabled={action.busy}
                onClick={() =>
                  void action.run(async () => {
                    setIssued(null);
                    setPairing(await api.counters.pairingCode(counter.id));
                  })
                }
              >
                {plain ? 'Connect a PC' : 'Pairing code'}
              </Button>
              {/* A key typed in by hand is a support job, so a merchant has only the code. */}
              {!plain && (
                <Button
                  variant="ghost"
                  disabled={action.busy}
                  title="Issue a key directly, for a counter that cannot use a pairing code"
                  onClick={() =>
                    void action.run(async () => {
                      setPairing(null);
                      setIssued(await api.counters.issueKey(counter.id));
                      onChanged();
                    })
                  }
                >
                  Issue key
                </Button>
              )}
            </>
          )}
          <ArchiveButton
            status={counter.status}
            label={counter.name}
            cascadeNote={
              plain
                ? 'It stops taking mobile payments straight away. Restoring it later needs no new code.'
                : 'This till stops taking payments immediately. Its key survives, so restoring it does not need re-pairing.'
            }
            busy={action.busy}
            onArchive={() => void action.run(() => api.counters.archive(counter.id)).then(onChanged)}
            onRestore={() => void action.run(() => api.counters.restore(counter.id)).then(onChanged)}
          />
        </div>
      }
    >
      <ErrorBanner message={action.error} />

      {plain ? (
        <div className="mb-3 text-muted">{describeConnection(counter, live.length)}</div>
      ) : (
        <>
          <div className="mb-1.5 text-muted">
            {archived
              ? 'Archived — this counter cannot authenticate.'
              : live.length === 0
                ? 'Not paired yet.'
                : `${live.length} active key${live.length > 1 ? 's' : ''}.`}
          </div>

          {/* What is actually running on that till. Written by the relay handshake, so a
              counter that has never connected says so rather than showing a stale guess. */}
          <div className="mb-3 text-muted">
            {counter.companionVersion ? (
              <>
                Companion <span className="font-mono text-text">{counter.companionVersion}</span>
                {counter.lastSeenAt && <> · last seen {describeLastSeen(counter.lastSeenAt)}</>}
              </>
            ) : (
              'Companion version unknown — this counter has not connected to the relay yet.'
            )}
          </div>

          <InlineEdit
            label="MPOS Counter ID"
            value={counter.mposCounterId}
            placeholder="01"
            hint="CounterMaster.CounterID in this customer's MPOS database. Nothing uses it yet — it is recorded for matching receipts later."
            busy={action.busy}
            onSave={(next) =>
              void action
                .run(() => api.counters.update(counter.id, { mposCounterId: next }))
                .then(onChanged)
            }
          />
        </>
      )}

      <Modal
        open={Boolean(pairing)}
        title={plain ? `Connect a PC to ${counter.name}` : `Pair ${counter.name}`}
        onClose={() => setPairing(null)}
      >
        {/* The merchant's steps name the companion's own buttons, which still say "pair". */}
        {pairing && (
          <ShowOnce
            label={
              plain
                ? 'On the till PC, open Mobile Pay settings, type this code under “Pairing code”, then press “Pair this counter”.'
                : 'Type this into the companion on that counter'
            }
            value={pairing.code}
            large
            note={
              plain
                ? `Works once, until ${new Date(pairing.expiresAt).toLocaleTimeString()}. Capitals, spaces and dashes do not matter.`
                : `Valid until ${new Date(pairing.expiresAt).toLocaleTimeString()}. Case and separators do not matter.`
            }
          />
        )}
      </Modal>

      <Modal
        open={Boolean(issued)}
        title={`API key for ${counter.name}`}
        onClose={() => setIssued(null)}
      >
        {issued && <ShowOnce label="Counter API key" value={issued.apiKey} />}
      </Modal>

      {live.length > 0 && plain && (
        <ConnectedPcs
          keys={live}
          busy={action.busy}
          onDisconnect={(keyId) =>
            void action.run(() => api.counters.revokeKey(counter.id, keyId)).then(onChanged)
          }
        />
      )}

      {live.length > 0 && !plain && (
        <TableScroll>
          <table className="mt-2.5">
            <thead>
              <tr>
                <th>Key</th>
                <th>Label</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {live.map((key) => (
                <tr key={key.keyId}>
                  <td className="font-mono text-xs break-all">{key.keyId}</td>
                  <td className="text-muted">{key.label || '—'}</td>
                  <td className="text-right">
                    {/* Revoking is the one genuinely irreversible action here: the key
                        is hashed, so it cannot be shown or reinstated afterwards. */}
                    <ConfirmButton
                      label="Revoke"
                      question={`Revoke key ${key.keyId}?`}
                      detail="This cannot be undone — that counter must pair again."
                      confirmLabel="Yes, revoke"
                      busy={action.busy}
                      onConfirm={() =>
                        void action
                          .run(() => api.counters.revokeKey(counter.id, key.keyId))
                          .then(onChanged)
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
    </Card>
  );
}

/**
 * A counter's keys as a merchant sees them: the PCs that can take payments as this
 * counter, by the name each gave when it paired. Pairing again adds a key rather than
 * replacing one, so a till PC that was swapped out keeps working until it is disconnected
 * here - which is the reason a merchant needs the list at all.
 */
function ConnectedPcs({
  keys,
  busy,
  onDisconnect,
}: {
  keys: CounterKey[];
  busy: boolean;
  onDisconnect: (keyId: string) => void;
}) {
  return (
    <TableScroll>
      <table className="mt-2.5">
        <thead>
          <tr>
            <th>Connected PC</th>
            <th>Since</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {keys.map((key) => {
            const pc = pcName(key);
            return (
              <tr key={key.keyId}>
                <td>{pc}</td>
                <td className="text-muted">{formatDate(key.createdAt)}</td>
                <td className="text-right">
                  <ConfirmButton
                    label="Disconnect"
                    question={`Disconnect ${pc}?`}
                    detail="It stops taking mobile payments as this counter. To use it again, connect it with a new code."
                    confirmLabel="Yes, disconnect"
                    busy={busy}
                    onConfirm={() => onDisconnect(key.keyId)}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </TableScroll>
  );
}

/** Pairing labels a key "paired: <machine name>"; a key issued by hand has no machine. */
function pcName(key: CounterKey): string {
  const machine = key.label?.startsWith('paired: ') ? key.label.slice('paired: '.length) : '';
  return machine || 'A PC set up without a code';
}

/**
 * The counter's state in a sentence. "Came online" rather than "last seen": the time is
 * written when the till connects, not while it stays connected, so a till that has been
 * up for days shows the day it started rather than looking offline.
 */
function describeConnection(counter: Counter, connected: number): string {
  if (counter.status === 'archived') return 'Archived. This counter cannot take mobile payments.';
  if (connected === 0) return 'Not set up yet. Press “Connect a PC” to get a code.';

  const parts = [`${connected} PC${connected === 1 ? '' : 's'} connected`];
  parts.push(
    counter.lastSeenAt ? `came online ${describeLastSeen(counter.lastSeenAt)}` : 'not online yet',
  );
  // "0.9.3+1b10975": the build stamp after the plus is for staff.
  if (counter.companionVersion) parts.push(`Mobile Pay ${counter.companionVersion.split('+')[0]}`);
  return parts.join(' · ');
}

/**
 * "4 minutes ago" beats a timestamp here: the question is whether this till is talking to
 * us now, and a reader should not have to subtract times in their head to answer it.
 */
function describeLastSeen(iso: string): string {
  const seen = new Date(iso);
  const minutes = Math.round((Date.now() - seen.getTime()) / 60000);

  if (!Number.isFinite(minutes)) return seen.toLocaleString();
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  if (minutes < 60 * 24) {
    const hours = Math.round(minutes / 60);
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }
  return seen.toLocaleDateString();
}
